// src/pages/NotificationsPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    warning: 0,
    success: 0,
    danger: 0,
    info: 0
  });
  
  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Charger l'utilisateur
  useEffect(() => {
    const loadUser = () => {
      try {
        const stored = localStorage.getItem("user");
        if (stored) {
          const userData = JSON.parse(stored);
          setUser(userData);
        }
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    };
    
    loadUser();
  }, []);

  // Charger les notifications
  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError('Non authentifié');
        setLoading(false);
        return;
      }

      // Vérifier si admin
      if (!user || Number(user.profile) !== 1) {
        setError('Accès réservé aux administrateurs');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/auth/notifications.php`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data.success) {
        const notifs = response.data.data.notifications || [];
        setNotifications(notifs);
        
        // Calculer les statistiques
        const statsData = {
          total: notifs.length,
          warning: notifs.filter(n => n.type === 'warning').length,
          success: notifs.filter(n => n.type === 'success').length,
          danger: notifs.filter(n => n.type === 'danger').length,
          info: notifs.filter(n => n.type === 'info').length
        };
        setStats(statsData);
      } else {
        setError('Erreur lors du chargement des notifications');
      }
    } catch (error) {
      console.error("Erreur:", error);
      
      if (error.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        setError('Accès non autorisé');
      } else if (error.code === 'ECONNABORTED') {
        setError('Timeout: Le serveur met trop de temps à répondre');
      } else {
        setError('Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  // Filtrer les notifications par type
  const [filter, setFilter] = useState('all');
  
  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications.filter(n => n.type === filter);

  // Si pas admin
  if (user && Number(user.profile) !== 1) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Notifications</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item active">Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#ffebee',
                  color: COLORS.danger
                }}>
                  <i className="bi bi-shield-lock" style={{ fontSize: '2rem' }}></i>
                </div>
                <h5 style={{ color: COLORS.primary, fontWeight: '600' }}>Accès non autorisé</h5>
                <p style={{ color: COLORS.darkText }}>
                  Cette page est réservée aux administrateurs.
                </p>
              </div>
              <Link 
                to="/dashboard" 
                className="btn fw-semibold"
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-house me-2"></i>
                Retour au tableau de bord
              </Link>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Notifications</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Notifications</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {/* Cartes de statistiques */}
        <div className="row mb-4">
          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderLeft: `4px solid ${COLORS.primary}`
            }}>
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-uppercase mb-1" style={{ 
                      fontSize: '0.8rem',
                      color: COLORS.primary,
                      fontWeight: '600'
                    }}>
                      Total
                    </div>
                    <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                      {stats.total}
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-bell fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderLeft: `4px solid ${COLORS.warning}`
            }}>
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-uppercase mb-1" style={{ 
                      fontSize: '0.8rem',
                      color: COLORS.warning,
                      fontWeight: '600'
                    }}>
                      Avertissements
                    </div>
                    <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                      {stats.warning}
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#fff8e1',
                      color: COLORS.warning
                    }}>
                      <i className="bi bi-exclamation-triangle fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderLeft: `4px solid ${COLORS.success}`
            }}>
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-uppercase mb-1" style={{ 
                      fontSize: '0.8rem',
                      color: COLORS.success,
                      fontWeight: '600'
                    }}>
                      Succès
                    </div>
                    <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                      {stats.success}
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#e8f5e9',
                      color: COLORS.success
                    }}>
                      <i className="bi bi-check-circle fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-4">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderLeft: `4px solid ${COLORS.danger}`
            }}>
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-uppercase mb-1" style={{ 
                      fontSize: '0.8rem',
                      color: COLORS.danger,
                      fontWeight: '600'
                    }}>
                      Alertes
                    </div>
                    <div className="h3 mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                      {stats.danger}
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                      width: '50px',
                      height: '50px',
                      backgroundColor: '#ffebee',
                      color: COLORS.danger
                    }}>
                      <i className="bi bi-x-circle fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="row">
          <div className="col-lg-12">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                {/* En-tête avec filtres */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0" style={{ 
                    color: COLORS.primary,
                    borderBottom: `2px solid ${COLORS.lightBg}`,
                    paddingBottom: '16px',
                    width: '100%'
                  }}>
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                        width: '36px',
                        height: '36px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-bell"></i>
                      </div>
                      Toutes les notifications
                    </div>
                  </h5>
                </div>

                {/* Filtres */}
                <div className="d-flex flex-wrap gap-2 mb-4">
                  <button 
                    className={`btn fw-semibold ${filter === 'all' ? '' : 'btn-outline-primary'}`}
                    onClick={() => setFilter('all')}
                    style={filter === 'all' ? {
                      color: 'white',
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem'
                    } : {
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (filter !== 'all') {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filter !== 'all') {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }
                    }}
                  >
                    Tous ({stats.total})
                  </button>
                  
                  <button 
                    className={`btn fw-semibold ${filter === 'warning' ? '' : 'btn-outline-warning'}`}
                    onClick={() => setFilter('warning')}
                    style={filter === 'warning' ? {
                      color: '#ff8f00',
                      backgroundColor: '#fff8e1',
                      borderColor: '#ffc107',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem'
                    } : {
                      color: '#ff9800',
                      borderColor: '#ff9800',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (filter !== 'warning') {
                        e.target.style.backgroundColor = '#fff8e1';
                        e.target.style.color = '#ff8f00';
                        e.target.style.borderColor = '#ffc107';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filter !== 'warning') {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#ff9800';
                        e.target.style.borderColor = '#ff9800';
                      }
                    }}
                  >
                    Avertissements ({stats.warning})
                  </button>
                  
                  <button 
                    className={`btn fw-semibold ${filter === 'success' ? '' : 'btn-outline-success'}`}
                    onClick={() => setFilter('success')}
                    style={filter === 'success' ? {
                      color: '#2e7d32',
                      backgroundColor: '#e8f5e9',
                      borderColor: '#4caf50',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem'
                    } : {
                      color: '#4caf50',
                      borderColor: '#4caf50',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (filter !== 'success') {
                        e.target.style.backgroundColor = '#e8f5e9';
                        e.target.style.color = '#2e7d32';
                        e.target.style.borderColor = '#4caf50';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filter !== 'success') {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#4caf50';
                        e.target.style.borderColor = '#4caf50';
                      }
                    }}
                  >
                    Succès ({stats.success})
                  </button>
                  
                  <button 
                    className={`btn fw-semibold ${filter === 'info' ? '' : 'btn-outline-info'}`}
                    onClick={() => setFilter('info')}
                    style={filter === 'info' ? {
                      color: COLORS.primary,
                      backgroundColor: '#E3F2FD',
                      borderColor: COLORS.secondary,
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem'
                    } : {
                      color: COLORS.secondary,
                      borderColor: COLORS.secondary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (filter !== 'info') {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.color = COLORS.primary;
                        e.target.style.borderColor = COLORS.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filter !== 'info') {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.secondary;
                        e.target.style.borderColor = COLORS.secondary;
                      }
                    }}
                  >
                    Infos ({stats.info})
                  </button>
                  
                  <button 
                    className={`btn fw-semibold ${filter === 'danger' ? '' : 'btn-outline-danger'}`}
                    onClick={() => setFilter('danger')}
                    style={filter === 'danger' ? {
                      color: '#c62828',
                      backgroundColor: '#ffebee',
                      borderColor: '#f44336',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem'
                    } : {
                      color: '#f44336',
                      borderColor: '#f44336',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (filter !== 'danger') {
                        e.target.style.backgroundColor = '#ffebee';
                        e.target.style.color = '#c62828';
                        e.target.style.borderColor = '#f44336';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filter !== 'danger') {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#f44336';
                        e.target.style.borderColor = '#f44336';
                      }
                    }}
                  >
                    Alertes ({stats.danger})
                  </button>
                  
                  <button 
                    className="btn fw-semibold"
                    onClick={loadNotifications}
                    disabled={loading}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      transition: 'all 0.3s ease',
                      marginLeft: 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }
                    }}
                  >
                    <i className={`bi ${loading ? 'bi-arrow-clockwise spin' : 'bi-arrow-clockwise'} me-1`}></i>
                    {loading ? ' Chargement...' : ' Actualiser'}
                  </button>
                </div>

                {/* État de chargement/erreur */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des notifications...</p>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger text-center py-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                        width: '60px',
                        height: '60px',
                        backgroundColor: '#ffebee',
                        color: COLORS.danger
                      }}>
                        <i className="bi bi-exclamation-octagon fs-2"></i>
                      </div>
                      <h5 className="alert-heading">Erreur</h5>
                      <p>{error}</p>
                    </div>
                    <button 
                      className="btn fw-semibold"
                      onClick={loadNotifications}
                      style={{
                        color: COLORS.danger,
                        borderColor: COLORS.danger,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '8px 20px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.danger;
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.danger;
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Réessayer
                    </button>
                  </div>
                ) : filteredNotifications.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-bell-slash fs-1"></i>
                      </div>
                    </div>
                    <h5 className="mb-3" style={{ color: COLORS.primary }}>Aucune notification</h5>
                    <p className="text-muted" style={{ color: COLORS.darkText }}>
                      {filter === 'all' 
                        ? "Vous n'avez aucune notification pour le moment." 
                        : `Aucune notification de type "${filter}"`}
                    </p>
                    {filter !== 'all' && (
                      <button 
                        className="btn fw-semibold mt-3"
                        onClick={() => setFilter('all')}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#E3F2FD';
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = COLORS.primary;
                        }}
                      >
                        Voir toutes les notifications
                      </button>
                    )}
                  </div>
                ) : (
                  /* Liste des notifications */
                  <div className="list-group">
                    {filteredNotifications.map((notif) => {
                      // Déterminer les styles selon le type
                      const typeStyles = {
                        warning: {
                          borderLeftColor: COLORS.warning,
                          iconBg: '#fff8e1',
                          iconColor: '#ff9800',
                          badgeBg: '#fff8e1',
                          badgeColor: '#ff8f00'
                        },
                        success: {
                          borderLeftColor: COLORS.success,
                          iconBg: '#e8f5e9',
                          iconColor: '#4caf50',
                          badgeBg: '#e8f5e9',
                          badgeColor: '#2e7d32'
                        },
                        danger: {
                          borderLeftColor: COLORS.danger,
                          iconBg: '#ffebee',
                          iconColor: '#f44336',
                          badgeBg: '#ffebee',
                          badgeColor: '#c62828'
                        },
                        info: {
                          borderLeftColor: COLORS.secondary,
                          iconBg: '#E3F2FD',
                          iconColor: COLORS.secondary,
                          badgeBg: '#E3F2FD',
                          badgeColor: COLORS.primary
                        }
                      };
                      
                      const styles = typeStyles[notif.type] || typeStyles.info;
                      
                      return (
                        <div 
                          key={notif.id} 
                          className="list-group-item list-group-item-action mb-3"
                          style={{ 
                            borderRadius: '8px',
                            border: `1px solid ${COLORS.lightBg}`,
                            borderLeft: `4px solid ${styles.borderLeftColor}`,
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.08)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.04)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div className="d-flex w-100 justify-content-between align-items-start">
                            <div className="d-flex align-items-start w-100">
                              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                width: '48px',
                                height: '48px',
                                backgroundColor: styles.iconBg,
                                color: styles.iconColor,
                                flexShrink: 0
                              }}>
                                <i className={`bi bi-${notif.icon || 'info-circle'} fs-4`}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <h6 className="mb-0 fw-semibold" style={{ color: COLORS.primary }}>
                                    {notif.title}
                                  </h6>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: styles.badgeBg,
                                    color: styles.badgeColor,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem'
                                  }}>
                                    {notif.time}
                                  </span>
                                </div>
                                <p className="mb-2" style={{ color: COLORS.darkText }}>
                                  {notif.message}
                                </p>
                                
                                {notif.link && (
                                  <div className="mt-3">
                                    <Link 
                                      to={notif.link} 
                                      className="btn fw-semibold"
                                      style={{
                                        color: styles.iconColor,
                                        borderColor: styles.iconColor,
                                        backgroundColor: 'transparent',
                                        borderRadius: '6px',
                                        padding: '6px 16px',
                                        fontSize: '0.85rem',
                                        transition: 'all 0.3s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = styles.iconColor;
                                        e.target.style.color = 'white';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.color = styles.iconColor;
                                      }}
                                    >
                                      <i className="bi bi-arrow-right me-1"></i>
                                      Voir les détails
                                    </Link>
                                  </div>
                                )}
                              </div>
                            </div>
                            {notif.badge && (
                              <div className="ms-3">
                                <span className="badge fw-semibold" style={{ 
                                  backgroundColor: styles.badgeBg,
                                  color: styles.badgeColor,
                                  padding: '8px 16px',
                                  borderRadius: '6px',
                                  fontSize: '0.85rem'
                                }}>
                                  {notif.badge}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pied de page */}
                {!loading && !error && filteredNotifications.length > 0 && (
                  <div className="mt-4 pt-3 border-top" style={{ borderColor: COLORS.lightBg }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted" style={{ color: '#6c757d' }}>
                          Affichage de {filteredNotifications.length} notification(s)
                          {filter !== 'all' && ` (filtrées par type: ${filter})`}
                        </small>
                      </div>
                      <div>
                        <small className="text-muted" style={{ color: '#6c757d' }}>
                          Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}
                        </small>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Information pour les admins */}
            <div className="card mt-4" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              backgroundColor: '#E3F2FD'
            }}>
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-2 text-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center" style={{ 
                      width: '60px',
                      height: '60px',
                      backgroundColor: COLORS.primary,
                      color: 'white'
                    }}>
                      <i className="bi bi-info-circle fs-3"></i>
                    </div>
                  </div>
                  <div className="col-md-10">
                    <h6 style={{ color: COLORS.primary, marginBottom: '8px', fontWeight: '600' }}>
                      Information pour les administrateurs
                    </h6>
                    <p style={{ color: COLORS.darkText, fontSize: '0.95rem', marginBottom: '0' }}>
                      Les notifications sont générées automatiquement en fonction des activités du système. 
                      Elles incluent les sociétés en attente de validation, les emails non confirmés, 
                      et autres événements nécessitant votre attention.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .alert-heading,
        h6.fw-semibold {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        p,
        small,
        .badge,
        .list-group-item {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(11, 60, 93, 0.25);
        }
        
        .btn,
        .badge,
        .list-group-item {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .bi-arrow-clockwise.spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.flex-wrap {
            justify-content: center;
          }
          
          .btn {
            margin-bottom: 8px;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .col-md-2 {
            margin-bottom: 1rem;
          }
          
          .list-group-item {
            flex-direction: column;
            align-items: flex-start !important;
          }
          
          .rounded-circle.me-3 {
            margin-bottom: 1rem;
          }
        }
        
        /* Amélioration de la lisibilité */
        .card-title {
          letter-spacing: 0.3px;
        }
        
        .badge {
          font-weight: 500;
          letter-spacing: 0.3px;
        }
        
        .list-group-item h6 {
          line-height: 1.4;
        }
      `}</style>
    </Layout>
  );
};

export default NotificationsPage;